import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Copy, MessageCircle, Facebook, Twitter, Share2, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

const APP_URL = (typeof window !== "undefined" && window.location.origin) || "";

export default function ShareCard({ need }) {
  const [open, setOpen] = useState(false);
  const url = `${APP_URL}/needs/${need.id}`;
  const text = need.intent === "need"
    ? `Can anyone help? ${need.title} — ${need.location || "WA"}. Recommend someone?`
    : `Sharing on African Bridge Media: ${need.title}${need.location ? " · " + need.location : ""}.`;
  const share = async (channel) => {
    let target = "";
    const encoded = encodeURIComponent(`${text}\n\n${url}`);
    if (channel === "whatsapp") target = `https://wa.me/?text=${encoded}`;
    else if (channel === "facebook") target = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;
    else if (channel === "x") target = `https://twitter.com/intent/tweet?text=${encoded}`;
    else if (channel === "sms") target = `sms:?&body=${encoded}`;
    else if (channel === "copy") {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Link copied");
      api.post(`/needs/${need.id}/share`).catch(() => {});
      return;
    }
    if (target) {
      window.open(target, "_blank", "noopener,noreferrer");
      api.post(`/needs/${need.id}/share`).catch(() => {});
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="share-open" className="abm-btn-pine rounded-full h-11 w-full font-medium">
          <Share2 className="w-4 h-4 mr-2" /> Ask your community
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1E3A2F]">Share this ask</DialogTitle>
          <DialogDescription>Send it to a group and let the community connect the dots.</DialogDescription>
        </DialogHeader>
        <div className="rounded-2xl border border-[#E2DED0] p-5 bg-[#F7F4EB] text-sm text-[#1a1a1a] whitespace-pre-wrap" data-testid="share-preview">
          {text}
          {"\n\n"}
          <span className="text-[#C05A3D] underline">{url}</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <ShareBtn testid="share-whatsapp" onClick={() => share("whatsapp")} icon={<MessageCircle className="w-4 h-4" />} label="WhatsApp" bg="bg-[#25D366] text-white" />
          <ShareBtn testid="share-facebook" onClick={() => share("facebook")} icon={<Facebook className="w-4 h-4" />} label="Facebook" bg="bg-[#1877F2] text-white" />
          <ShareBtn testid="share-x" onClick={() => share("x")} icon={<Twitter className="w-4 h-4" />} label="X / Twitter" bg="bg-[#0f1419] text-white" />
          <ShareBtn testid="share-sms" onClick={() => share("sms")} icon={<MessageCircle className="w-4 h-4" />} label="Text (SMS)" bg="bg-[#1E3A2F] text-white" />
          <ShareBtn testid="share-copy" onClick={() => share("copy")} icon={<Copy className="w-4 h-4" />} label="Copy link" bg="bg-white border border-[#E2DED0] text-[#1E3A2F]" />
          <ShareBtn testid="share-native" onClick={async () => {
            if (navigator.share) {
              try { await navigator.share({ title: need.title, text, url }); api.post(`/needs/${need.id}/share`).catch(() => {}); }
              catch {}
            } else { share("copy"); }
          }} icon={<LinkIcon className="w-4 h-4" />} label="More…" bg="bg-white border border-[#E2DED0] text-[#1E3A2F]" />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ShareBtn({ icon, label, onClick, bg, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 rounded-full h-11 px-3 text-sm font-medium ${bg} hover:opacity-95 transition`}
    >
      {icon} {label}
    </button>
  );
}
